import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MoneyInput } from '../MoneyInput';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Pill, Plus, X, AlertTriangle, Star } from 'lucide-react';
import PurchaseInvoiceForm, { emptyComprobante } from '../PurchaseInvoiceForm';

export function NewReceptionDialog({
  open, onOpenChange, onCancel, onSubmit,
  formData, setFormData, pendingOrders, selectedOrder, handleOrderSelect,
  isMedicamento, totalReceivedForArticle, expectedForArticle, handleAddLot, handleRemoveLine,
  unitOf = () => 'unidad',
  comprobante, setComprobante, extraComprobantes, setExtraComprobantes,
  suppliers, accounts, expenseConcepts, withholdingTypes,
  canLoadComprobantes = true,
  tolerancePct = 0,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva Recepción</DialogTitle>
          <DialogDescription>Registra la recepción de mercadería contra una Orden de Compra</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label>Orden de Compra *</Label>
            <select
              value={formData.order_id}
              onChange={(e) => handleOrderSelect(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              required
            >
              <option value="">Selecciona una OC</option>
              {pendingOrders.map(o => (
                <option key={o.order_id} value={o.order_id}>
                  {o.order_number} - {o.supplier} ({o.items?.length} items)
                </option>
              ))}
            </select>
          </div>

          {selectedOrder && (
            <>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="font-medium mb-2">Proveedor: {selectedOrder.supplier}</p>
                <p className="text-sm text-muted-foreground">NP Origen: {selectedOrder.request_number}</p>
              </div>

              <div className="border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Artículo</TableHead>
                      <TableHead className="text-center">Autorizado (OC)</TableHead>
                      <TableHead className="text-center">Recibido</TableHead>
                      {canLoadComprobantes && <TableHead className="text-center">Precio Neto Unitario ($) *</TableHead>}
                      <TableHead className="text-center">Vencimiento</TableHead>
                      <TableHead className="text-center">Nro. Lote</TableHead>
                      <TableHead className="text-center w-20"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => {
                      const med = isMedicamento(item);
                      const totalRecv = totalReceivedForArticle(item.article_id);
                      const expected = expectedForArticle(item.article_id);
                      const excess = totalRecv > expected;
                      const hasOtherLines = formData.items.filter(i => i.article_id === item.article_id).length > 1;
                      const isExtra = item.quantity_expected === 0;
                      const noQty = (parseInt(item.quantity_received) || 0) <= 0;
                      return (
                        <TableRow key={index} className={excess ? 'bg-red-50' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span>{item.article_name}</span>
                              {med && <Pill className="w-4 h-4 text-rose-500" title="Medicamento" />}
                              {hasOtherLines && <Badge variant="outline" className="text-xs">multi-lote</Badge>}
                            </div>
                            <span className="text-xs text-muted-foreground">Unidad: {unitOf(item.article_id)}</span>
                          </TableCell>
                          <TableCell className="text-center">{item.quantity_expected || '—'}</TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              min="0"
                              value={item.quantity_received}
                              onChange={(e) => {
                                const newItems = [...formData.items];
                                newItems[index].quantity_received = e.target.value === '' ? '' : (parseInt(e.target.value) || 0);
                                setFormData({ ...formData, items: newItems });
                              }}
                              className="w-20 text-center mx-auto"
                              data-testid={`reception-qty-${index}`}
                            />
                          </TableCell>
                          {canLoadComprobantes && (
                            <TableCell className="text-center">
                              <MoneyInput
                                value={item.cost_price}
                                onChange={(e) => {
                                  const newItems = [...formData.items];
                                  newItems[index].cost_price = e.target.value;
                                  setFormData({ ...formData, items: newItems });
                                }}
                                className="w-28 text-right mx-auto"
                                disabled={noQty}
                                title={noQty ? 'Sin cantidad recibida: no requiere precio' : undefined}
                              />
                            </TableCell>
                          )}
                          <TableCell className="text-center">
                            <Input
                              type="date"
                              min={new Date().toISOString().slice(0,10)}
                              value={item.expiration_date || ''}
                              onChange={(e) => {
                                const newItems = [...formData.items];
                                newItems[index].expiration_date = e.target.value;
                                setFormData({ ...formData, items: newItems });
                              }}
                              className="w-36 text-center mx-auto"
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="text"
                              value={item.lot_number || ''}
                              onChange={(e) => {
                                const newItems = [...formData.items];
                                newItems[index].lot_number = e.target.value;
                                setFormData({ ...formData, items: newItems });
                              }}
                              placeholder={med && !noQty ? 'Obligatorio' : 'Opcional'}
                              className={`w-32 text-center mx-auto ${med && !noQty && !item.lot_number ? 'border-red-300' : ''}`}
                              data-testid={`reception-lot-${index}`}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-1">
                              {!isExtra && item.article_id && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleAddLot(index)}
                                  title="Agregar otro lote del mismo artículo"
                                  data-testid={`add-lot-${index}`}
                                >
                                  <Plus className="w-4 h-4 text-blue-600" />
                                </Button>
                              )}
                              {isExtra && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveLine(index)}
                                  title="Eliminar este renglón"
                                >
                                  <X className="w-4 h-4 text-red-600" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {(() => {
                  const seen = new Set();
                  const rows = [];
                  formData.items.forEach(it => {
                    if (!it.article_id || seen.has(it.article_id)) return;
                    seen.add(it.article_id);
                    const total = totalReceivedForArticle(it.article_id);
                    const exp = expectedForArticle(it.article_id);
                    if (total !== exp) {
                      rows.push({ name: it.article_name, total, exp, excess: total > exp });
                    }
                  });
                  if (rows.length === 0) return null;
                  return (
                    <div className="p-3 border-t bg-slate-50 space-y-1">
                      {rows.map((r, i) => (
                        <div key={i} className={`text-sm flex items-center gap-2 ${r.excess ? 'text-red-700' : 'text-amber-700'}`}>
                          <AlertTriangle className="w-4 h-4" />
                          <span><b>{r.name}:</b> esperado {r.exp}, recibido {r.total} ({r.excess ? `exceso +${r.total - r.exp}` : `faltan ${r.exp - r.total}`})</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {(() => {
                const tol = (tolerancePct || 0) / 100;
                const seen = new Set();
                let excess = false, shortages = 0;
                formData.items.forEach(it => {
                  if (!it.article_id || seen.has(it.article_id)) return;
                  seen.add(it.article_id);
                  const exp = expectedForArticle(it.article_id);
                  const recv = totalReceivedForArticle(it.article_id);
                  if (recv > exp && (recv - exp) > exp * tol) excess = true;
                  else if (recv < exp && (exp - recv) > exp * tol) shortages++;
                });
                const incident = !!formData.incident_reported;
                const status = (excess || incident) ? 'conditional' : (shortages > 0 ? 'partial' : 'complete');
                const cfg = {
                  complete: { label: 'Completa', cls: 'bg-green-100 text-green-800', desc: 'Las cantidades coinciden con lo autorizado. El stock impacta al confirmar.' },
                  partial: { label: 'Parcial', cls: 'bg-orange-100 text-orange-800', desc: `${shortages} artículo(s) con faltante. El stock de lo recibido impacta igual y la OC cierra como "Recibida c/Faltante". La reposición se gestiona con una nueva NP.` },
                  conditional: { label: 'Condicionada', cls: 'bg-amber-100 text-amber-800', desc: incident ? 'Incidencia reportada: la recepción queda retenida hasta revisión de un supervisor.' : 'Exceso sobre lo autorizado: la recepción queda retenida hasta revisión de un supervisor.' },
                }[status];
                return (
                  <div className="border rounded-md p-3 bg-slate-50 space-y-1" data-testid="auto-status-panel">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">Estado de la recepción (automático):</span>
                      <Badge className={cfg.cls} data-testid="auto-status-badge">{cfg.label}</Badge>
                      {tolerancePct > 0 && <span className="text-xs text-muted-foreground">Tolerancia de desvío: {tolerancePct}%</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{cfg.desc}</p>
                  </div>
                );
              })()}

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formData.incident_reported}
                    onChange={(e) => setFormData({ ...formData, incident_reported: e.target.checked })}
                    data-testid="incident-checkbox"
                  />
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Reportar incidencia (artículo defectuoso, vencido, embalaje dañado, etc.)
                </label>
                <div>
                  <Label htmlFor="deviation_notes">{formData.incident_reported ? 'Descripción de la incidencia *' : 'Observaciones (opcional)'}</Label>
                  <Textarea
                    id="deviation_notes"
                    value={formData.deviation_notes}
                    onChange={(e) => setFormData({ ...formData, deviation_notes: e.target.value })}
                    rows={2}
                    placeholder={formData.incident_reported ? 'Describí el problema detectado...' : 'Ej: el proveedor avisó que no enviará el resto'}
                    required={!!formData.incident_reported}
                    data-testid="deviation-notes-input"
                  />
                </div>
              </div>

              {selectedOrder?.supplier_id && (
                <div className="border rounded-md p-4 bg-yellow-50/50 space-y-3" data-testid="supplier-evaluation-section">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-600" />
                    <Label className="text-base font-semibold">Evaluación del Proveedor (obligatoria) *</Label>
                  </div>
                  <p className="text-xs text-muted-foreground">Puntuá de 1 (deficiente) a 5 (excelente) cada criterio.</p>
                  {[
                    { key: 'product_conformity', label: 'Conformidad del Producto' },
                    { key: 'delivery_compliance', label: 'Cumplimiento de Plazos' },
                    { key: 'order_integrity', label: 'Integridad del Pedido' }
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between gap-4">
                      <span className="text-sm font-medium">{label}</span>
                      <div className="flex items-center gap-1" data-testid={`rating-${key}`}>
                        {[1,2,3,4,5].map(n => (
                          <button
                            type="button"
                            key={n}
                            onClick={() => setFormData({ ...formData, evaluation: { ...formData.evaluation, [key]: n } })}
                            className="p-1 transition-transform hover:scale-110"
                            data-testid={`rating-${key}-${n}`}
                            aria-label={`${label} ${n}`}
                          >
                            <Star className={`w-6 h-6 ${n <= formData.evaluation[key] ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                          </button>
                        ))}
                        <span className="ml-2 text-sm font-mono w-6">{formData.evaluation[key]}</span>
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 flex items-center justify-between border-t">
                    <span className="text-sm font-medium">Puntaje promedio</span>
                    <span className="text-lg font-bold text-yellow-700">
                      {((formData.evaluation.product_conformity + formData.evaluation.delivery_compliance + formData.evaluation.order_integrity) / 3).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <Label htmlFor="eval_observations" className="text-xs">Observaciones (opcional)</Label>
                    <Textarea
                      id="eval_observations"
                      value={formData.evaluation.observations}
                      onChange={(e) => setFormData({ ...formData, evaluation: { ...formData.evaluation, observations: e.target.value } })}
                      rows={2}
                      placeholder="Comentarios sobre el desempeño del proveedor..."
                    />
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {canLoadComprobantes ? (
                  <>
                    <div>
                      <Label className="text-base font-semibold">Comprobantes Fiscales <span className="text-muted-foreground font-normal text-sm">(opcional, hasta varios)</span></Label>
                      <p className="text-xs text-muted-foreground">Cargá los datos fiscales del comprobante. Se imputa a <b>Bienes de Cambio</b>, figura en el Libro IVA Compras, <b>actualiza la cuenta corriente del proveedor</b> y <b>registra el asiento en el Libro Diario</b>. Si el proveedor mandó más de una factura, sumá las que necesites con <i>Agregar otro comprobante</i>.</p>
                    </div>
                    <PurchaseInvoiceForm
                      value={comprobante}
                      onChange={setComprobante}
                      suppliers={suppliers}
                      accounts={accounts}
                      expenseConcepts={expenseConcepts}
                      withholdingTypes={withholdingTypes}
                      imputationMode={false}
                      showConcept={false}
                    />
                    {extraComprobantes.map((extra, eidx) => (
                      <div key={eidx} className="border-2 border-dashed rounded-md p-3 relative">
                        <button
                          type="button"
                          onClick={() => setExtraComprobantes(prev => prev.filter((_, i) => i !== eidx))}
                          className="absolute top-2 right-2 text-red-600 hover:text-red-800 text-xs"
                          data-testid={`remove-extra-comp-${eidx}`}
                          title="Quitar este comprobante"
                        >✕ Quitar</button>
                        <p className="text-sm font-semibold text-blue-700 mb-2">Comprobante adicional #{eidx + 2}</p>
                        <PurchaseInvoiceForm
                          value={extra}
                          onChange={(nv) => setExtraComprobantes(prev => prev.map((p, i) => i === eidx ? (typeof nv === 'function' ? nv(p) : nv) : p))}
                          suppliers={suppliers}
                          accounts={accounts}
                          expenseConcepts={expenseConcepts}
                          withholdingTypes={withholdingTypes}
                          imputationMode={false}
                          showConcept={false}
                        />
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setExtraComprobantes(prev => [...prev, emptyComprobante()])}
                      data-testid="add-extra-comprobante-btn"
                    >
                      + Agregar otro comprobante
                    </Button>
                  </>
                ) : (
                  <div className="border rounded-md p-3 bg-slate-50 text-xs text-muted-foreground" data-testid="comprobante-locked-note">
                    La carga del <b>comprobante fiscal</b> y del <b>precio neto unitario</b> la realiza Administración. Registrá la mercadería con sus cantidades, lotes y vencimientos; la valorización y los datos impositivos se cargan después desde el detalle de la recepción.
                  </div>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>Cancelar</Button>
            <Button type="submit" disabled={!formData.order_id}>Registrar Recepción</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
